import React, { useCallback, useEffect, useRef, useState } from 'react';
import './FullNewsWidget.css';
import useResizeNotifier from '../../hooks/useResizeNotifier';
import useContainerWidth from '../../hooks/useContainerWidth';
import { NewsHeadlineType, NewsStoryType } from '../../types/news.type';
import NewsStory from '../../Components/News/NewsStory';
import { ReactComponent as SearchIcon } from '../../assets/icons/search-line.svg';
import { ReactComponent as RefreshIcon } from '../../assets/icons/refresh.svg';
import useHeightBasedItems from '../../hooks/useHeightBasedItems';
import useElementDimension from '../../hooks/useElementDimension';
import Modal from '../../Components/Common/Modal/Modal';
import NewsList from '../../Components/News/NewsList';
import { getHeadlines, getNewsStory } from '../../api/news.api';
import RicInputBox from '../../Components/Common/RicInputBox/RicInputBox';
import CommonTooltip from '../../Components/Common/CommonTooltip/CommonTooltip';
import {
  BaseWidgetProps,
  CommonTooltipType,
  WidgetHeightMessage,
} from '../../types/common.type';
import { Region, WidgetMessageType, WidgetNameSource } from '../../utils/type';

import Loading from '../../Components/Loading/Loading';
import { ReactComponent as ErrorIcon } from '../../assets/icons/error-state.svg';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import { errorMsg } from '../../constants/common.constant';
import Layout from '../../Components/Common/Layout/Layout';
import { ThemeType } from '../../utils/type';

export interface FullNewsWidgetProps extends BaseWidgetProps {
  theme?: ThemeType;
  token?: string;
  region?: Region;
  sector?: string;
  frDate?: string;
  toDate?: string;
  onHeightChange?: (message: WidgetHeightMessage) => void;
}

const FullNewsWidget = (props: FullNewsWidgetProps) => {
  const { onHeightChange, token } = props;
  const theme = props.theme ?? ThemeType.Direct;
  const touch = matchMedia('(hover: none)').matches;
  const MAXIMUM_RESULTS_PER_PAGE = 5;
  const reqNewsNumber = 100;
  const widgetRef = useRef<HTMLDivElement | null>(null);
  useResizeNotifier(widgetRef, WidgetNameSource.FullNews, onHeightChange);

  const headlinesRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const msgItem = errorMsg.find((obj) => obj.key === 'newsWidgets');
  const [isInitial, setIsInitial] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const onLoadingChangeRef = useRef(props.onLoadingChange);
  onLoadingChangeRef.current = props.onLoadingChange;
  useEffect(() => {
    onLoadingChangeRef.current?.(isLoading);
  }, [isLoading]);
  const [isEmptyData, setIsEmptyData] = useState(false);
  const { isSmallScreen } = useContainerWidth(widgetRef);
  const [openModal, setOpenModal] = useState(false);
  const [symbol, setSymbol] = useState<string>('');
  const [nid, setNid] = useState<string>('');
  const [newsStory, setNewsStory] = useState<NewsStoryType | null>();
  const [narrowNewsStory, setNarrowNewsStory] =
    useState<NewsStoryType | null>();
  const [data, setData] = useState<NewsHeadlineType[]>([]);
  const [isLast, setIsLast] = useState(false);
  const [page, setPage] = useState(1);
  const { height } = useElementDimension(headlinesRef);
  const [tooltip, setTooltip] = useState<CommonTooltipType>({
    content: '',
    show: false,
    x: 0,
    y: 0,
  });
  const [resetTrigger, setResetTrigger] = useState(0);

  const {
    displayedItems,
    itemsPerPage: PER_PAGE,
    totalPages,
    isMeasuring,
  } = useHeightBasedItems(headlinesRef, data, page, [
    data,
    height,
    isSmallScreen,
  ]);

  useEffect(() => {
    setIsInitial(true);
  }, [props.region, props.sector, props.frDate, props.toDate]);

  useEffect(() => {
    setIsInitial(true);
  }, [symbol]);

  useEffect(() => {
    if (!isInitial) return;
    setNid('');
    setNewsStory(null);
    setNarrowNewsStory(null);
    setTooltip({
      content: '',
      show: false,
      x: 0,
      y: 0,
    });
  }, [isInitial]);

  useEffect(() => {
    if (!isInitial) return;
    let cancelled = false;
    const run = async () => {
      try {
        setIsLoading(true);
        setIsEmptyData(false);
        const res = await getHeadlines({
          token: props.token || '',
          symbol: symbol,
          limit: reqNewsNumber,
          region: props.region || 'AU',
          sector: props.sector,
          frdate: props.frDate,
          todate: props.toDate,
        });
        if (cancelled) return;
        if (res.newsHeadlines[0]) {
          setNid(res.newsHeadlines[0].newsId);
        }
        setData(res.newsHeadlines);
        setIsLast(res.newsHeadlines.length !== reqNewsNumber);
        setPage(1);
        setIsEmptyData(res.newsHeadlines.length === 0);
        if (res.newsHeadlines.length === 0) {
          props.onNoData?.({
            type: WidgetMessageType.NoData,
            source: 'FullNewsWidget',
            data: null,
          });
        }
      } catch (err) {
        console.log(err);
        props.onError?.({ message: err as string, type: 'API_ERROR' });
        setIsEmptyData(true);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsInitial(false);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [isInitial]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!nid) return;
      try {
        const res = await getNewsStory(props.token || '', nid);
        if (cancelled) return;
        if (res.story) {
          setNewsStory(res.story);
          setNarrowNewsStory(res.story);
        }
      } catch (err) {
        console.log(err);
        props.onError?.({ message: err as string, type: 'API_ERROR' });
      } finally {
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [nid, isSmallScreen]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (
        isMeasuring || // totalPages is a transient 1 while measuring
        isLast ||
        data.length === 0 ||
        totalPages - page > MAXIMUM_RESULTS_PER_PAGE
      )
        return;
      try {
        const last = data[data.length - 1];
        const res = await getHeadlines({
          token: props.token || '',
          limit: reqNewsNumber,
          symbol: symbol,
          region: props.region || 'AU',
          sector: props.sector,
          newsId: last.newsId,
          newsRT: last.rt,
          frdate: props.frDate,
          todate: props.toDate,
        });
        if (cancelled) return;
        setData((prev) => [...prev, ...res.newsHeadlines]);
        if (res.newsHeadlines.length !== reqNewsNumber) setIsLast(true);
      } catch (err) {
        console.log(err);
        props.onError?.({ message: err as string, type: 'API_ERROR' });
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [page, PER_PAGE, totalPages, isLast, isMeasuring]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleHeadlineClick = async (newsId: string) => {
    if (nid && nid !== newsId) {
      if (isSmallScreen) setNarrowNewsStory(null);
    }
    setNid(newsId);
    if (isSmallScreen) {
      setOpenModal(true);
    }
  };

  const handleSearchChange = (value: string) => {
    setSymbol(value);
  };

  const handleSearchClear = () => {
    setSymbol('');
  };

  const handleRefresh = () => {
    // setResetTrigger(prev => prev + 1);
    // if (symbol === "") {
    setIsInitial(true);
    // }
  };

  const handleModalClose = useCallback(() => {
    setOpenModal(false);
  }, []);

  const isLoadingState = isLoading && !props.onLoadingChange;
  const showEmptyState = !isLoadingState && isEmptyData;

  return (
    <Layout theme={theme}>
      <div className="fullNews-container" ref={widgetRef}>
        <div className="fullNews">
          {/* <div className="fullNews-header">
                    <span className="fullNews-header-title">News</span>
                    <LSEG />
                </div> */}

          <div
            className="fullNews-content-container"
            style={{ display: isSmallScreen && openModal ? 'none' : 'flex' }}
          >
            <div className="fullNews-content">
              <div
                className={`left-content${
                  showEmptyState || isLoadingState
                    ? ' left-content--no-border'
                    : ''
                }`}
              >
                <div
                  className="fullNews-menu"
                  style={{
                    display: isSmallScreen && openModal ? 'none' : 'flex',
                  }}
                >
                  {/* <div className="fullNews-menu-region">
                        <Dropdown
                            options={countryMenu}
                            initialValue={selectedIndex} // Setting an initial value
                        // onSelect={handleRegionSelect}
                        />
                    </div>
                    <div className="fullNews-menu-sector">
                        <MultipleSelect options={NEWS_CATEGORY} onSelect={handleCategoryChange} placeholder="All News" allSelectLabel="All News" />
                    </div> */}
                  <div className="fullNews-menu-search">
                    <SearchIcon className="fullNews-menu-search-icon" />
                    <div className="fullNews-menu-search-input-wrapper">
                      <RicInputBox
                        placeholder="Search by Ticker or Company name"
                        onSelect={handleSearchChange}
                        onClear={handleSearchClear}
                        region={props.region}
                        sector={props.sector}
                        token={token}
                        resetTrigger={resetTrigger}
                      />
                    </div>
                  </div>
                  <div className="fullNews-menu-refresh">
                    <RefreshIcon
                      className="fullNews-menu-refresh-icon"
                      onClick={handleRefresh}
                    />
                  </div>
                </div>
                {!isLoadingState && !(!isLoading && isEmptyData) && (
                  <NewsList
                    ref={headlinesRef}
                    tooltipRef={tooltipRef}
                    touch={touch}
                    setTooltip={setTooltip}
                    displayedItems={displayedItems}
                    totalPages={totalPages}
                    selectNewsStory={handleHeadlineClick}
                    page={page}
                    setPage={setPage}
                    isSmallScreen={isSmallScreen}
                    widgetRef={widgetRef}
                    hidePagination={false}
                    isLast={isLast}
                  />
                )}
              </div>
              <div className="fullNews-content-story">
                {newsStory ? (
                  <NewsStory
                    headline={newsStory.headline}
                    date={newsStory.date}
                    time={newsStory.time}
                    story={newsStory.body}
                  />
                ) : null}
              </div>
            </div>
            {isLoadingState ? (
              <div className="fullNews-state-container">
                <Loading />
              </div>
            ) : showEmptyState ? (
              <div className="fullNews-state-container">
                <ErrorMessage msg={msgItem} />
              </div>
            ) : null}
          </div>
          {isSmallScreen && openModal && narrowNewsStory ? (
            <div className="mobile-story-fullscreen">
              <NewsStory
                headline={narrowNewsStory.headline}
                date={narrowNewsStory.date}
                time={narrowNewsStory.time}
                story={narrowNewsStory.body}
                withCloseButton={true}
                onClose={handleModalClose}
              />
            </div>
          ) : null}
        </div>
        <CommonTooltip
          ref={tooltipRef}
          content={tooltip.content}
          show={tooltip.show}
          x={tooltip.x}
          y={tooltip.y}
        />
      </div>
    </Layout>
  );
};

export default FullNewsWidget;
